import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import {
  TransitionExcludedStatusApi,
  TransitionExcludedStatus,
  NewTransitionExcludedStatus,
  TransitionExcludedStatusFilters,
} from '../models';

const transitionExcludedStatusesService = createApi({
  reducerPath: 'transitionExcludedStatusesService',
  baseQuery: createBaseQueryWithReauth(
    `${API_BUSINESS_URL}/transition-excluded-statuses`
  ),
  tagTypes: ['TransitionExcludedStatuses', 'Transitions'],
  endpoints: (builder) => ({
    getTransitionExcludedStatuses: builder.query<
      TransitionExcludedStatus[],
      { filters?: TransitionExcludedStatusFilters }
    >({
      query: ({ filters }) => {
        const params: Record<string, string> = {};
        if (filters?.transitionId && filters.transitionId.trim() !== '') {
          params.transitionId = filters.transitionId.trim();
        }
        return {
          url: '',
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
      transformResponse: (response: TransitionExcludedStatusApi[]) =>
        response.map((item) => ({
          id: item.id,
          transitionId: item.transitionId,
          excludedStatusId: item.excludedStatusId,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        })),
      providesTags: ['TransitionExcludedStatuses'],
    }),
    getTransitionExcludedStatusById: builder.query<
      TransitionExcludedStatus,
      { excludedStatusId: string }
    >({
      query: ({ excludedStatusId }) => ({
        url: `/${excludedStatusId}`,
      }),
      transformResponse: (response: TransitionExcludedStatusApi) => ({
        id: response.id,
        transitionId: response.transitionId,
        excludedStatusId: response.excludedStatusId,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      providesTags: (_result, _error, { excludedStatusId }) => [
        { type: 'TransitionExcludedStatuses' as const, id: excludedStatusId },
      ],
    }),
    createTransitionExcludedStatus: builder.mutation<
      TransitionExcludedStatus,
      NewTransitionExcludedStatus
    >({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      transformResponse: (response: TransitionExcludedStatusApi) => ({
        id: response.id,
        transitionId: response.transitionId,
        excludedStatusId: response.excludedStatusId,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      invalidatesTags: ['TransitionExcludedStatuses', 'Transitions'],
    }),
    deleteTransitionExcludedStatus: builder.mutation<void, string>({
      query: (excludedStatusId) => ({
        url: `/${excludedStatusId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TransitionExcludedStatuses', 'Transitions'],
    }),
  }),
});

export default transitionExcludedStatusesService;
export const {
  useGetTransitionExcludedStatusesQuery,
  useGetTransitionExcludedStatusByIdQuery,
  useCreateTransitionExcludedStatusMutation,
  useDeleteTransitionExcludedStatusMutation,
} = transitionExcludedStatusesService;
